import React, { useCallback } from 'react'

import { GetServerSideProps, NextPage } from 'next'

import { CompanyParams } from '@services/api/companies'

import { CommonServerSideProps as ServerSide } from 'types/common-serverside-props'

import { Skeleton } from '@components/skeleton'

import styles from '@styles/pages/empresas.module.css'
import { StatesApi } from '@services/api/states'
import { CitiesApi } from '@services/api/cities'
import { FranchiseProvider } from 'context/franchise'
import { FranchisePage } from '@components/pages/fornecedores/franchise-page'
import { FranchisePageList } from '@components/pages/fornecedores/franchise-page-list'

import { IndexBlockCallDto } from '@services/api/block-calls'

import { Franchise, FranchiseIndexDto } from '@services/api/franchise/types'
import { FranchiseService } from '@services/franchise'

import { FaWhatsapp } from 'react-icons/fa'
import Link from 'next/link'
import { Location } from '@services/api/locations'
import SEO from '@components/shared/seo'
import { FaqFornecedores } from '@components/pages/fornecedores/faq-fornecedores'
import { BreadCrumb } from '@components/breadcumb'
import { useRouter } from 'next/router'

interface IndexProps extends ServerSide {
  responseFranchisePagination: FranchiseIndexDto
  params: CompanyParams
  isMap: boolean
  franchise: Franchise
  blockCallsVideo: IndexBlockCallDto
  location: Location
}

const Lista: NextPage<IndexProps> = ({
  params,
  menus,
  responseFranchisePagination,
  isMap,
  franchise,
  blockCallsVideo,
  location
}) => {
  const route = useRouter()
  const getUrlWhatsApp = useCallback(
    (cellphone: string) =>
      `https://wa.me/55${cellphone.replace(
        /\D/g,
        ''
      )}?text=Olá, gostaria de mais informações sobre Energia Solar.`,
    [franchise?.mobile]
  )

  const SEOSettings = () => {
    const { name, state } = location || { name: undefined, state: undefined }
    let title = 'Empresas de Energia Solar | Unidades Portal Solar'
    let description =
      'Encontre Instaladores e Empresas de Energia Solar Fotovoltaica Perto de Você. O melhor atendimento e serviço de instalação de energia solar do Brasil. Clique e Confira!'

    if (name) {
      title = `Empresas de Energia Solar em ${name} | Unidades Portal Solar`
      description = `Você está procurando empresas de energia solar fotovoltaica para instalar painéis solares em ${name}? Veja as unidades Portal Solar em ${name} e solicite um orçamento grátis de instalação de placas solares em ${name}.`
    }

    if (state) {
      title = `Empresas de Energia Solar em ${name} - ${state.name} | Unidades Portal Solar`
      description = `Você está procurando empresas de energia solar fotovoltaica para instalar painéis solares em ${name} - ${state.name}? Veja as unidades Portal Solar em ${name} - ${state.name} e solicite um orçamento grátis de instalação de placas solares em ${name} - ${state.name}.`
    }

    return <SEO title={title} description={description} />
  }

  return (
    <>
      <Skeleton head={{ title: 'Empresas' }} menus={menus}>
        {!franchise && <BreadCrumb path={route.asPath.split('?')[0]} />}
        <FranchiseProvider
          responseFranchisePagination={responseFranchisePagination}
          isMap={isMap}
          params={params}
          franchise={franchise}
          blockCallsVideo={blockCallsVideo}
          location={location}
        >
          {!franchise ? (
            <div>
              <div className={styles.pageContainer}>
                <SEOSettings />
                <FranchisePageList />
              </div>
              <div className="bg-red">
                <FaqFornecedores />
              </div>
            </div>
          ) : (
            <FranchisePage />
          )}
        </FranchiseProvider>
      </Skeleton>
      {franchise && franchise?.mobile && (
        <div className="fixed bottom-0 w-full bg-white p-4 border-t border-orange z-10">
          <div className="flex justify-center md:justify-end max-w-7xl mx-auto">
            <Link
              href={getUrlWhatsApp(franchise?.mobile)}
              className="bg-green px-2 rounded-full text-white flex items-center hover:opacity-80"
              target="_blank"
            >
              <span className="px-7 py-3">fale com essa unidade</span>
              <FaWhatsapp className="text-4xl" />
            </Link>
          </div>
        </div>
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  params: _params,
  query
}) => {
  try {
    const [energySolar, stateSlug, citySlug = '', slugFranchise] =
      _params.slugs as string[]
    const SLUG_ENERGY_SOLAR = 'empresas-de-energia-solar'
    const verifyEnerySolar = energySolar === SLUG_ENERGY_SOLAR

    // validate path equals /empresas-de-energia-solar
    if (!verifyEnerySolar) {
      return {
        notFound: true
      }
    }

    // validate path only /empresas-de-energia-solar
    if (!stateSlug && !citySlug) {
      return await FranchiseService.findFranchises({
        page: query.page as string,
        map: query.map as string
      })
    }

    const state = await StatesApi.show(stateSlug)
      .then(response => response.data)
      .catch(() => undefined)

    // validate path /empresas-de-energia-solar/slug-state isn't valid
    if (!state) {
      return {
        redirect: {
          destination: '/fornecedores/empresas-de-energia-solar',
          permanent: false
        }
      }
    }

    // validate path /empresas-de-energia-solar/slug-state don't have slug-city
    // get only franchises in state selected
    if (!citySlug) {
      return await FranchiseService.findFranchises({
        page: query.page as string,
        map: query.map as string,
        params: {
          search: {
            'city[state[slug]]': stateSlug
          }
        },
        location: state
      })
    }

    const city = await CitiesApi.show(citySlug, state.id)
      .then(_result => _result.data)
      .catch(() => undefined)

    // validate path /empresas-de-energia-solar/slug-state/slug-city isn't valid
    if (!city) {
      return {
        redirect: {
          destination: `/fornecedores/empresas-de-energia-solar/${stateSlug}`,
          permanent: false
        }
      }
    }

    // validate path /empresas-de-energia-solar/slug-state/slug-franchise is valid
    if (slugFranchise) {
      return await FranchiseService.validateFranchise(slugFranchise)
    }

    return await FranchiseService.findFranchises({
      page: query.page as string,
      map: query.map as string,
      params: {
        search: {
          'city[slug]': citySlug,
          'city[state[slug]]': stateSlug
        }
      },
      location: city
    })
  } catch (error) {
    console.error(error)
    return {
      notFound: true
    }
  }
}

export default Lista
